import * as React from 'react';
import styled from 'styled-components';
import { ensureArray } from '@agilepoint/utils-array';
import {
    IDateParts, IDate, isDateValid, getDateParts, getToday, IDateFormat, isDateInRange,
    sortDatesByDateParts, getDateTimeFromDateParts, getDateFormat
} from '@agilepoint/utils-date-time';
import { excludeTimeComponent, getNextMonthInfo, getPrevMonthInfo, isSameDate } from './utils';
import { ICalendarProps, Day, IDateInfo, CalendarView } from './calendar-types';
import { IDropdownOption } from '../dropdown';
import { DateGrid } from './parts/dategrid';
import { CalendarToolbar } from './parts/calendar-toolbar';
import { CalendarMonthNavigator } from './parts/calendar-month-navigator';
import { selectThemeSemantics } from '../../styling/theming';
import { withCulture } from '../../localization';
import { StackPanel } from '../stack-panel';

const CalendarWrapper = styled.div`
    display: inline-block;
    user-select: none;
    padding: ${selectThemeSemantics(({ gap }) => gap.xl)};
`;

const toJsDate = (date: IDateParts) => getDateTimeFromDateParts(date).toJSDate();

interface ICalendarState {
    view: CalendarView;
    value: IDateParts[];
}

function getValuesUsingMode(value: IDateParts[], props: ICalendarProps) {
    const { selectionMode } = props;
    if (selectionMode === 'range') {
        return value.length > 2 ? value.slice(0, 2) : ensureArray(value);
    } else if (selectionMode === 'single') {
        return value.length > 1 ? value.slice(0, 1) : ensureArray(value);
    }
    return ensureArray(value);
}

function getSelectedDatePartsFromValue(value: IDate[], props: ICalendarBaseProps) {
    const { minDate, maxDate, format, culture } = props;
    const dateFormat = format || getDateFormat(culture);
    const values = ensureArray(value);
    const selectedDatePartsValue = values.reduce((selectedDateParts: IDateParts[], selectedDate) => {
        if (isDateValid(selectedDate, dateFormat)) {
            const { date, month, year } = getDateParts(selectedDate, dateFormat);
            if (isDateInRange(excludeTimeComponent(selectedDate, dateFormat)!, excludeTimeComponent(minDate, dateFormat), excludeTimeComponent(maxDate, dateFormat), dateFormat)) {
                selectedDateParts.push({ date, month, year });
            }
        }
        return selectedDateParts;
    }, []);
    return getValuesUsingMode(selectedDatePartsValue, props);
}

function getSelectedDateValues(values: IDate[], format?: IDateFormat) {
    return values.reduce((selectedDateParts: IDateParts[], selectedDate) => {
        const isInputValueValid = isDateValid(selectedDate, format);
        const { date, month, year } = getDateParts(isInputValueValid ? selectedDate! : getToday(), format);
        selectedDateParts.push({ date, month, year });
        return selectedDateParts;
    }, []);
}

function getSelectedDateView(selectedDateValues: IDateParts[], view: CalendarView) {
    if (view) {
        return view;
    } else if (selectedDateValues.length > 0) {
        return selectedDateValues[0];
    }
    return getDateParts(getToday());
}

function isDateGreaterThanReference(date: IDateInfo, reference: CalendarView) {
    if (date.year < reference.year) {
        return false;
    }
    if (date.year === reference.year) {
        return date.month >= reference.month;
    }
    return true;
}

function isDateLessThanReference(date: IDateInfo, reference: CalendarView) {
    if (date.year > reference.year) {
        return false;
    }
    if (date.year === reference.year) {
        return date.month <= reference.month;
    }
    return true;
}

interface ICalendarBaseProps extends ICalendarProps {
    culture?: string;
}

class CalendarBase extends React.PureComponent<ICalendarBaseProps, ICalendarState> {
    static getDerivedStateFromProps(props: ICalendarBaseProps, state: ICalendarState) {
        const { value } = props;
        const { value: stateValue } = state;
        if (value) {
            const values = ensureArray(value);
            const selectedDatePartsArray = getSelectedDatePartsFromValue(values, props);
            return {
                value: selectedDatePartsArray
            };
        } else if (stateValue.length > 0) {
            const selectedDatePartsArray = getValuesUsingMode(stateValue, props);
            return {
                value: selectedDatePartsArray
            };
        }
        return null;
    }

    constructor(props: ICalendarProps) {
        super(props);
        const { defaultValue = [], value, view } = props;
        const values = ensureArray(value || defaultValue);
        const filteredValues = values.filter(currentValue => currentValue);
        const selectedDateValues = getSelectedDateValues(filteredValues, this.getDateFormat());

        const calendarView = getSelectedDateView(selectedDateValues, view!);

        this.state = {
            value: selectedDateValues,
            view: {
                month: calendarView.month,
                year: calendarView.year
            }
        };
    }

    componentDidMount() {
        const { minDate, maxDate } = this.props;
        const { value, view: { month, year } } = this.state;
        const format = this.getDateFormat();

        if (value.length || !minDate || !isSameDate(format, minDate, maxDate)) {
            return;
        }
        const dateParts = getDateParts(minDate, format);
        this.onDateSelect({
            ...dateParts,
            isInCurentMonth: (dateParts.month === month && dateParts.year === year)
        });
    }

    componentDidUpdate(prevProps: ICalendarBaseProps) {
        const { value, view } = this.props;
        const format = getDateFormat();
        if (prevProps.value !== value) {
            const values = ensureArray(value);
            const selectedDateValues = values.reduce((selectedDateParts: IDateParts[], selectedDate) => {
                if (selectedDate && isDateValid(selectedDate, format)) {
                    const { date, month, year } = getDateParts(selectedDate, format);
                    selectedDateParts.push({ date, month, year });
                }
                return selectedDateParts;
            }, []);

            const calendarView = getSelectedDateView(selectedDateValues, view!);

            this.setState({
                view: {
                    month: calendarView.month,
                    year: calendarView.year
                }
            });
        }
    }

    render() {
        const { className } = this.props;
        return (
            <CalendarWrapper className={className}>
                {this.renderToolbar()}
                {this.renderMonthInfo()}
                {this.renderDateCalendarGrid()}
            </CalendarWrapper>
        );
    }

    private renderToolbar() {
        const { localization = { todayLabel: 'Today' }, showTodayButton, startYear, endYear } = this.props;
        const { view: { year } } = this.state;
        return (
            <CalendarToolbar
                todayButtonLabel={localization.todayLabel}
                selectedYear={year}
                onTodayClick={this.setTodayDate}
                onYearChange={this.onYearChange}
                showTodayButton={showTodayButton}
                startYear={startYear}
                endYear={endYear}
            />
        );
    }

    private onYearChange = (selectedKeys: React.Key[], selectedValues: IDropdownOption[]) => {
        const { view } = this.state;
        if (selectedValues.length === 0) {
            return;
        }
        const newViewStateValue = {
            ...view,
            year: Number(selectedValues[0].key)
        };

        this.setState({
            view: newViewStateValue
        });

        this.triggerOnViewChange(newViewStateValue);
    }

    private renderMonthInfo() {
        const { culture, localization } = this.props;
        const monthsInView = this.getMonthsInView();
        return (
            <CalendarMonthNavigator
                culture={culture}
                onPreviousMonthClick={this.naviagteToPrevMonth}
                onNextMonthClick={this.naviagteToNextMonth}
                previousMonthTitle={localization?.previousMonthTitle}
                nextMonthTitle={localization?.nextMonthTitle}
                monthsInView={monthsInView} />
        );
    }

    private naviagteToNextMonth = (ev: React.MouseEvent) => {
        ev.preventDefault();
        const { view: { month, year } } = this.state;
        const { view: propsView } = this.props;
        const newViewStateValue = getNextMonthInfo(month, year, this.getNumberOfMonthsInView());
        if (!propsView) {
            this.setState({ view: newViewStateValue });
        }
        this.triggerOnViewChange(newViewStateValue);
    }

    private naviagteToPrevMonth = (ev: React.MouseEvent) => {
        ev.preventDefault();
        const { view: { month, year } } = this.state;
        const { view: propsView } = this.props;
        const newViewStateValue = getPrevMonthInfo(month, year, this.getNumberOfMonthsInView());
        if (!propsView) {
            this.setState({ view: newViewStateValue });
        }
        this.triggerOnViewChange(newViewStateValue);
    }

    private renderDateCalendarGrid() {
        const {
            weekStartDay = Day.sun, selectionMode, culture, onRenderItem, weekDayNameLength, hideDateFromOtherMonth,
            showWeekNumber
        } = this.props;
        const { value } = this.state;
        const monthsInView = this.getMonthsInView();
        return (
            <StackPanel orientation="horizontal">
                {monthsInView.map((monthInfo) => (
                    <DateGrid
                        showWeekNumber={showWeekNumber}
                        key={`${monthInfo.month}_${monthInfo.year}`}
                        hideDateFromOtherMonth={hideDateFromOtherMonth}
                        weekStartDay={weekStartDay}
                        culture={culture}
                        onDateSelect={this.onDateSelect}
                        onDateUnselect={this.onDateUnselect}
                        onDateCellMouseOver={this.onDateCellMouseOver}
                        value={value}
                        view={monthInfo}
                        selectionMode={selectionMode}
                        isDateInRange={this.isDateInRange}
                        onRenderItem={onRenderItem}
                        weekDayNameLength={weekDayNameLength}
                    />
                ))}
            </StackPanel>
        );
    }

    private onDateCellMouseOver = (hoveredItem: IDateInfo) => {
        const { onDateCellMouseOver } = this.props;
        if (onDateCellMouseOver === undefined) {
            return;
        }
        onDateCellMouseOver(hoveredItem);
    }

    private onDateSelect = (selectedItem: IDateInfo) => {
        const { value } = this.state;
        const { date, month, year } = selectedItem;

        const selectedDateParts = sortDatesByDateParts(this.getSelectedDatePartsUsingMode(value, {
            date,
            month,
            year
        }));

        this.setState({ value: selectedDateParts });

        if (!this.isDateInView(selectedItem)) {
            this.setState({
                view: {
                    month,
                    year
                }
            });
        }

        const selectedDates = selectedDateParts.map(toJsDate);
        this.triggerOnChange(selectedDates);
    }

    private onDateUnselect = (selectedItem: IDateInfo) => {
        const { view: { year, month }, value } = this.state;
        let newValueState = {
            date: selectedItem.date,
            month,
            year
        };
        let newViewState = {
            month,
            year
        };
        if (!selectedItem.isInCurentMonth) {
            newViewState = (selectedItem.date < 15 ? getNextMonthInfo(month, year) : getPrevMonthInfo(month, year));
            newValueState = {
                ...newValueState,
                ...newViewState
            };
        }
        const selectedDateParts = sortDatesByDateParts(this.removeSelectedDateOnUnselection(value!, newValueState));
        this.setState({ value: selectedDateParts, view: newViewState } as ICalendarState);
        const selectedDates = selectedDateParts.map(toJsDate);
        this.triggerOnChange(selectedDates);
    }

    private isDateInView(date: IDateInfo) {
        const monthsInView = this.getMonthsInView();
        const first = monthsInView.at(0);
        const last = monthsInView.at(-1);
        if (!first || !last) {
            return false;
        }
        return (isDateGreaterThanReference(date, first) && isDateLessThanReference(date, last));
    }

    private triggerOnChange(value: Date[]) {
        const { onChange } = this.props;
        if (onChange === undefined) {
            return;
        }
        onChange(value);
    }

    private triggerOnViewChange(view: CalendarView) {
        const { onViewChange } = this.props;
        if (onViewChange === undefined) {
            return;
        }
        onViewChange(view);
    }

    private getSelectedDatePartsUsingMode = (value: IDateParts[], newSelectedValue: IDateParts) => {
        const { selectionMode } = this.props;
        if (selectionMode === 'multiple') {
            return [...value, newSelectedValue];
        } else if (selectionMode === 'range') {
            return this.getSelectedDatePartsOnRangeMode(value, newSelectedValue);
        }
        return ensureArray(newSelectedValue);
    }

    private isSelectedValuePresent = (value: IDateParts[], newSelectedValue: IDateParts) => value.some((dateParts) => {
        const { date, month, year } = dateParts;
        return (newSelectedValue.year === year && newSelectedValue.month === month && newSelectedValue.date === date);
    })

    private getSelectedDatePartsOnRangeMode = (value: IDateParts[], newSelectedValue: IDateParts) => {
        const isValueAlreadyPresent = this.isSelectedValuePresent(value, newSelectedValue);
        if (isValueAlreadyPresent) {
            return value;
        }
        return value.length < 2 ? [...value, newSelectedValue] : ensureArray(newSelectedValue);
    }

    private removeSelectedDateOnUnselection = (value: IDateParts[], newSelectedValue: { date: number, month: number, year: number }) => value.filter(dateParts => {
        const { date, month, year } = dateParts;
        return !(newSelectedValue.date === date && newSelectedValue.month === month && newSelectedValue.year === year);
    })

    private setTodayDate = () => {
        const today = getToday();
        const { value } = this.state;
        if (!this.isDateInRange(today)) {
            return;
        }
        const { date, month, year } = getDateParts(today);

        const todayDateParts = {
            date,
            month,
            year
        };

        const isTodayAlreadySelected = this.isTodaySelected(value!, todayDateParts);
        const selectedDateParts = sortDatesByDateParts(isTodayAlreadySelected ? ensureArray(value!) : this.getSelectedDatePartsUsingMode(value!, todayDateParts));

        this.setState({
            value: selectedDateParts,
            view: { month, year }
        });

        const selectedDates = selectedDateParts.map(toJsDate);
        this.triggerOnChange(selectedDates);
    }

    private isTodaySelected = (value: IDateParts[], todayDateParts: { date: number, month: number, year: number }) => value.some((dateParts) => {
        const { date, month, year } = dateParts;
        return todayDateParts.date === date && todayDateParts.month === month && todayDateParts.year === year;
    })

    private isDateInRange = (date: IDate) => {
        const { minDate, maxDate } = this.props;
        const format = getDateFormat();
        return isDateInRange(date, excludeTimeComponent(minDate, format), excludeTimeComponent(maxDate, format), format);
    }

    private getDateFormat() {
        const { format, culture } = this.props;
        return format || getDateFormat(culture);
    }

    private getNumberOfMonthsInView() {
        const { numberOfMonthsInView: numberOfMonthsInViewProps = 1 } = this.props;
        return Math.max(1, Math.min(3, numberOfMonthsInViewProps));
    }

    private getMonthsInView() {
        const { view: { month, year }, view } = this.state;
        const numberOfMonthsInView = this.getNumberOfMonthsInView();
        const monthsInView: CalendarView[] = [view];
        if (numberOfMonthsInView > 1) {
            monthsInView.push(getNextMonthInfo(month, year));
        }
        if (numberOfMonthsInView > 2) {
            monthsInView.unshift(getPrevMonthInfo(month, year));
        }
        return monthsInView;
    }
}

export const Calendar = withCulture(CalendarBase);
